/**
 * Journey page: Canvas Work Session (J3 + J4 + J5)
 *
 * Peak state: second agent just spawned, two agents working in parallel.
 * - J3: input was typed, task dispatched (single agent phase, now past)
 * - J4: Aria delegated to Rex via @mention (spawn just happened)
 * - J5: Rex + Aria both active, cursors visible, frames populating
 */

import { useState, useRef, useEffect } from "react";
import { useCanvasTransform } from "@/workshop/components/canvas/canvas-viewport";
import { WorkshopLayoutProvider } from "@/workshop/components/layout/layout-provider";
import { WorkshopMasterLayout } from "@/workshop/components/layout/master-layout";
import { AgentCursorOverlay } from "@/workshop/components/cursors/agent-cursor-overlay";
import { SearchFrame } from "@/workshop/components/shapes/search-frame/search-frame";
import { TextFrame } from "@/workshop/components/shapes/text-frame/text-frame";
import { EmailFrame } from "@/workshop/components/shapes/email-frame/email-frame";
import { WorkflowFrame } from "@/workshop/components/shapes/workflow-frame/workflow-frame";
import { ConversationFrame } from "@/workshop/components/shapes/conversation-frame/conversation-frame";
import {
  MOCK_CANVAS_SESSION,
  MOCK_SEARCH_RESULTS,
  MOCK_WORKFLOW_STEPS,
  MOCK_CONVERSATION_MESSAGES,
} from "@/workshop/data/mock-journeys";

// ─── Types ────────────────────────────────────────────────────────────────────

type Pos = { x: number; y: number };

// ─── Initial frame positions ──────────────────────────────────────────────────

const INITIAL_POS = {
  search: { x: 280, y: 60 } as Pos,
  text: { x: 520, y: 40 } as Pos,
  email: { x: 780, y: 60 } as Pos,
  workflow: { x: 280, y: 340 } as Pos,
  conversation: { x: 510, y: 340 } as Pos,
};

// ─── Canvas content ───────────────────────────────────────────────────────────

function CanvasContent() {
  // --- positions & drag ---
  const [pos, setPos] = useState(INITIAL_POS);
  const drag = useRef<{
    key: string;
    sx: number;
    sy: number;
    px: number;
    py: number;
  } | null>(null);
  const { zoom, isPanMode } = useCanvasTransform();
  const dotR = `${1 / zoom}px`;
  const dotSpacing = `${24 / zoom}px`;
  const zoomRef = useRef(zoom);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!drag.current) return;
      const { key, sx, sy, px, py } = drag.current;
      setPos((p) => ({
        ...p,
        [key]: {
          x: px + (e.clientX - sx) / zoomRef.current,
          y: py + (e.clientY - sy) / zoomRef.current,
        },
      }));
    };
    const up = () => {
      drag.current = null;
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, []);

  function grab(key: string, e: React.MouseEvent) {
    if (isPanMode) return;
    e.preventDefault();
    drag.current = {
      key,
      sx: e.clientX,
      sy: e.clientY,
      px: pos[key as keyof typeof pos].x,
      py: pos[key as keyof typeof pos].y,
    };
  }

  return (
    <div
      className="relative h-full w-full overflow-hidden bg-white dark:bg-black"
      data-testid="canvas-session-canvas"
      style={{
        backgroundImage: `radial-gradient(circle, var(--color-canvas-dot) ${dotR}, transparent ${dotR})`,
        backgroundSize: `${dotSpacing} ${dotSpacing}`,
      }}
    >
      {/* Frame: SearchFrame — done, showing results */}
      <div
        className="absolute z-10 cursor-grab active:cursor-grabbing select-none"
        style={{ left: pos.search.x, top: pos.search.y, width: 220 }}
        onMouseDown={(e) => grab("search", e)}
        data-testid="canvas-frame-search"
      >
        <SearchFrame
          query="AI content trends 2025"
          results={MOCK_SEARCH_RESULTS}
          status="done"
          agentName="Aria"
        />
      </div>

      {/* Frame: TextFrame — streaming by Aria */}
      <div
        className="absolute z-10 cursor-grab active:cursor-grabbing select-none"
        style={{ left: pos.text.x, top: pos.text.y, width: 240 }}
        onMouseDown={(e) => grab("text", e)}
        data-testid="canvas-frame-text"
      >
        <TextFrame
          title="Blog Outline"
          text={
            "## Introduction\nThe future of AI-powered content marketing is\n\n## Key Trends\n1. Autonomous multi-agent content pipelines\n2. Real-time personalisation at scale\n3. Human-in-the-loop review workflows"
          }
          status="streaming"
          agentName="Aria"
        />
      </div>

      {/* Frame: EmailFrame — streaming by Rex */}
      <div
        className="absolute z-10 cursor-grab active:cursor-grabbing select-none"
        style={{ left: pos.email.x, top: pos.email.y, width: 230 }}
        onMouseDown={(e) => grab("email", e)}
        data-testid="canvas-frame-email"
      >
        <EmailFrame
          title="Email Campaign"
          agentName="Rex"
          to="subscribers@acme.com"
          subject="What AI-powered teams are doing differently in 2025"
          body={
            "Hi there,\n\nWe're seeing a big shift in how marketing teams operate — and we wanted to share what's working…"
          }
          data-testid="email-frame-rex"
        />
      </div>

      {/* Frame: WorkflowFrame — idle, showing plan */}
      <div
        className="absolute z-10 cursor-grab active:cursor-grabbing select-none"
        style={{ left: pos.workflow.x, top: pos.workflow.y, width: 210 }}
        onMouseDown={(e) => grab("workflow", e)}
        data-testid="canvas-frame-workflow"
      >
        <WorkflowFrame
          title="Content Pipeline"
          flowSteps={MOCK_WORKFLOW_STEPS}
          status="idle"
          agentName="Aria"
        />
      </div>

      {/* Frame: ConversationFrame — agent chat */}
      <div
        className="absolute z-10 cursor-grab active:cursor-grabbing select-none"
        style={{
          left: pos.conversation.x,
          top: pos.conversation.y,
          width: 490,
        }}
        onMouseDown={(e) => grab("conversation", e)}
        data-testid="canvas-frame-conversation"
      >
        <ConversationFrame
          title="Agent Chat"
          agentName="Aria"
          messages={MOCK_CONVERSATION_MESSAGES}
          isStreaming={false}
          data-testid="conversation-frame-main"
        />
      </div>

      {/* Agent cursor overlay */}
      <AgentCursorOverlay
        cursors={MOCK_CANVAS_SESSION.cursors}
        data-testid="canvas-session-cursor-overlay"
      />
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function CanvasSessionPage() {
  return (
    <WorkshopLayoutProvider defaultSidebarOpen defaultRightPanelOpen>
      {/*
        SVG liquid-glass distortion filter — must live in the DOM for
        `backdrop-filter: url(#liquid-glass-distortion)` in index.css to work.
      */}
      <svg style={{ display: "none" }} aria-hidden="true">
        <defs>
          <filter
            id="liquid-glass-distortion"
            x="-10%"
            y="-10%"
            width="120%"
            height="120%"
            colorInterpolationFilters="sRGB"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.018 0.014"
              numOctaves="3"
              seed="5"
              result="noise"
            />
            <feColorMatrix
              type="saturate"
              values="0"
              in="noise"
              result="grayNoise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="grayNoise"
              scale="2"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>
      <div
        className="h-screen w-screen overflow-hidden"
        data-testid="canvas-session-page"
      >
        <WorkshopMasterLayout>
          <CanvasContent />
        </WorkshopMasterLayout>
      </div>
    </WorkshopLayoutProvider>
  );
}
